// Action queue types (contract `type`) → chip label, icon (components/ui icons.js)
// and tint class. Order = chip order.
export const ACTION_TYPES = {
  quote: { label: "Quotes", icon: "FileText", tint: "quote" },
  payment: { label: "Payments", icon: "CreditCard", tint: "payment" },
  invoice: { label: "Invoices", icon: "Receipt", tint: "order" },
  order: { label: "Orders", icon: "Package", tint: "order" },
  sell: { label: "Sell", icon: "Tag", tint: "sell" },
  collection: { label: "Collections", icon: "Truck", tint: "order" },
  listing: { label: "Listings", icon: "LayoutGrid", tint: "listing" },
  client: { label: "Clients", icon: "Heart", tint: "client" },
  followup: { label: "Follow-ups", icon: "Clock", tint: "listing" },
};

export const typeMeta = (type) => ACTION_TYPES[type] || { label: "Other", icon: "Flag", tint: "listing" };

// contract `tone` → Badge tone
export const BADGE_TONE = { danger: "danger", warning: "warning", info: "info", muted: "muted", accent: "accent" };

// Start-of-day tiles: [label, types it counts, tint]
export const BRIEF_TILES = [
  { label: "Payments to collect", types: ["payment", "invoice"], tint: "payment" },
  { label: "Quotes to move", types: ["quote"], tint: "quote" },
  { label: "Orders & sell", types: ["order", "sell", "collection"], tint: "order" },
  { label: "Listings & clients", types: ["listing", "client", "followup"], tint: "client" },
];

// ── Queue grouping (round 2) ─────────────────────────────────────────────────
// The queue is shown as: a strip of category tiles, "Now" (the 5 most urgent),
// one collapsible section per category (top 3 + "Show all N"), and a collapsed
// Backlog of everything older than 30 days — so 300-day-old balances and
// 100-day WatchFinder errors no longer flood the list.

export const NOW_CAP = 5;
export const SECTION_PREVIEW = 3;
export const STALE_DAYS = 30;

// Strip order. `collection` rides with Orders.
export const CATEGORIES = ["quote", "payment", "invoice", "order", "sell", "listing", "client", "followup"];
export const categoryOf = (type) => (type === "collection" ? "order" : CATEGORIES.includes(type) ? type : "followup");

/** The API's `stale` (round 2), else age > 30 days. */
export const isStale = (it) => (typeof it?.stale === "boolean" ? it.stale : Number(it?.age_days) > STALE_DAYS);

const byRank = (a, b) => (a.rank ?? 0) - (b.rank ?? 0);

/**
 * Split queue items (already filtered to a category, or all) into
 * { now, sections: [{cat, rows}], backlog: [{cat, rows}] }.
 * Now = up to NOW_CAP non-stale "now" items; everything else non-stale goes to
 * its category section; stale items go to the backlog.
 */
export function groupQueue(items, { cap = NOW_CAP } = {}) {
  const fresh = items.filter((it) => !isStale(it)).sort(byRank);
  const now = fresh.filter((it) => it.group === "now").slice(0, cap);
  const inNow = new Set(now.map((it) => it.key));
  const bucket = (rows) =>
    CATEGORIES.map((cat) => ({ cat, rows: rows.filter((it) => categoryOf(it.type) === cat) })).filter((s) => s.rows.length);
  return {
    now,
    sections: bucket(fresh.filter((it) => !inNow.has(it.key))),
    backlog: bucket(items.filter(isStale).sort(byRank)),
  };
}

/** The most urgent open item — what "Next up" shows. Never a backlog item. */
export const nextUp = (open) => groupQueue(open).now[0] || open.filter((it) => !isStale(it)).sort(byRank)[0] || null;
